/**
 * URL判定と抽出のユーティリティ関数
 */

import { getSiteUrl } from './metaTags';

/**
 * アクセス履歴の page_path を、現在の表示言語用のパスに変換する。
 * 英語ユーザーの履歴（/en/...）を日本語ユーザーが見たときは /ja/... に、
 * その逆も同様に遷移するようにする。
 * クエリ・ハッシュ付き（例: /en?category=python）の場合は二重に /en を付けないよう、
 * パス部分だけ置換してからクエリ・ハッシュをそのまま付ける。
 * @param pagePath API から返る page_path（例: /en/posts/slug, /ja/about, /en?category=python）
 * @param lang 現在の表示言語
 * @returns 表示言語用のパス（例: lang が 'ja' なら /ja/posts/slug）
 */
export const pagePathForDisplayLang = (pagePath: string, lang: 'ja' | 'en'): string => {
  if (!pagePath || typeof pagePath !== 'string') {
    return `/${lang}`;
  }
  const trimmed = pagePath.trim();
  const qIdx = trimmed.indexOf('?');
  const hIdx = trimmed.indexOf('#');
  const pathOnly = qIdx !== -1 ? trimmed.slice(0, qIdx) : (hIdx !== -1 ? trimmed.slice(0, hIdx) : trimmed);
  const queryHash = qIdx !== -1 ? trimmed.slice(qIdx) : (hIdx !== -1 ? trimmed.slice(hIdx) : '');
  if (/^\/(ja|en)(\/|$)/.test(pathOnly)) {
    const pathForLang = pathOnly.replace(/^\/(ja|en)(\/|$)/, `/${lang}$2`);
    return pathForLang + queryHash;
  }
  if (pathOnly.startsWith('/')) {
    return `/${lang}${pathOnly}${queryHash}`;
  }
  return `/${lang}/${pathOnly}${queryHash}`;
};
import { getUserPostsPath } from '../config/env';

/**
 * URLが自ドメインかどうかを判定
 * @param url 判定するURL（絶対URLまたは相対URL）
 * @returns 自ドメインの場合はtrue、それ以外はfalse
 */
export const isOwnDomainUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const siteUrl = getSiteUrl();
    if (!siteUrl) {
      return false;
    }

    // 相対URLの場合は自ドメインとみなす
    if (url.startsWith('/')) {
      return true;
    }

    // 絶対URLの場合、hostnameを比較
    const urlObj = new URL(url);
    const siteUrlObj = new URL(siteUrl);
    
    return urlObj.hostname === siteUrlObj.hostname;
  } catch {
    // URL解析に失敗した場合はfalseを返す
    return false;
  }
};

/**
 * 自ドメインURLからslugを抽出
 * @param url 自ドメインのURL（絶対URLまたは相対URL）
 * @param lang 言語（'ja' | 'en'）
 * @returns 抽出されたslug、抽出できない場合はnull
 */
export const extractSlugFromUrl = (url: string, lang: 'ja' | 'en'): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    const postsPath = getUserPostsPath();
    // パターン: /{lang}/posts/{slug} または /posts/{slug}
    // ハッシュやクエリパラメータは無視
    // まず指定された言語で試す
    let pattern = new RegExp(`/${lang}${postsPath}/([^/?#]+)`);
    let match = url.match(pattern);
    
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
    
    // 指定された言語でマッチしない場合、もう一方の言語で試す
    const otherLang = lang === 'ja' ? 'en' : 'ja';
    pattern = new RegExp(`/${otherLang}${postsPath}/([^/?#]+)`);
    match = url.match(pattern);
    
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
    
    return null;
  } catch {
    return null;
  }
};

/**
 * 自ドメインURLから言語コードを抽出
 * @param url 自ドメインのURL（絶対URLまたは相対URL）
 * @returns 抽出された言語コード（'ja' | 'en'）、抽出できない場合はnull
 */
export const extractLangFromUrl = (url: string): 'ja' | 'en' | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    const postsPath = getUserPostsPath();
    // パターン: /{lang}/posts/{slug} から言語コードを抽出
    const pattern = new RegExp(`/(ja|en)${postsPath}/`);
    const match = url.match(pattern);
    
    if (match && match[1]) {
      return match[1] as 'ja' | 'en';
    }
    
    return null;
  } catch {
    return null;
  }
};
